export const STOCK_MOVEMENT_REASONS: Record<string, string> = {
  manual_adjustment: 'Ajuste manual',
  stock_restock: 'Reposición',
  stock_initial_load: 'Carga inicial',
  purchase_reception: 'Compra',
  purchase_return: 'Devolución a proveedor',
  purchase_reception_void: 'Compra anulada',
  shrinkage: 'Merma',
  breakage: 'Rotura',
  expiry_writeoff: 'Vencimiento',
  return: 'Devolución',
  physical_count: 'Conteo físico',
  transfer_out: 'Transferencia enviada',
  transfer_in: 'Transferencia recibida',
  transfer_rejected: 'Transferencia rechazada',
  transfer_void_in: 'Transferencia anulada',
  transfer_void_out: 'Transferencia anulada',
  bulk_coefficient: 'Porcentaje',
  sale: 'Venta',
  sale_void: 'Venta anulada',
  cost_adjustment: 'Corrección de costo',
  variant_deactivated: 'Variante desactivada',
  production: 'Producción',
  production_consume: 'Consumo de producción',
  work_order: 'Orden de trabajo',
  // Reservas de pedidos online (release_stock_for_order escribe "<motivo>:<orderId>").
  reservation: 'Reserva de pedido',
  reservation_expired: 'Reserva vencida',
  reservation_expired_change: 'Reserva vencida',
  merchant_cancel: 'Reserva cancelada',
  unaccepted: 'Pedido no aceptado',
  pickup_noshow: 'Pedido no retirado',
  matching_exhausted: 'Pedido sin repartidor',
  release: 'Reserva liberada',
};

/** Label genérico: nunca exponer slugs crudos ni ids embebidos en la UI. */
const FALLBACK_MOVEMENT_LABEL = 'Movimiento de stock';

/**
 * Reasons tipo "<slug>:<referencia>" escritos por RPCs (sale:<saleId>,
 * reservation:<orderId>, reservation_expired:<orderId>, etc.).
 * Devuelve el slug base si el reason tiene referencia embebida.
 */
function reasonBaseSlug(reason: string): string | null {
  const idx = reason.indexOf(':');
  if (idx <= 0) return null;
  return reason.slice(0, idx);
}

/** Convierte reason de stock_movements a etiqueta para la UI (sale:uuid → "Venta", etc.). */
export function getStockMovementReasonLabel(reason: string | null): string {
  if (reason == null) return '';
  const trimmed = reason.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('sale:')) return STOCK_MOVEMENT_REASONS.sale;
  if (trimmed.startsWith('sale_void:')) return STOCK_MOVEMENT_REASONS.sale_void;
  if (trimmed.startsWith('production_consume:')) return STOCK_MOVEMENT_REASONS.production_consume;
  if (trimmed.startsWith('production:')) return STOCK_MOVEMENT_REASONS.production;
  const direct = STOCK_MOVEMENT_REASONS[trimmed];
  if (direct) return direct;
  const base = reasonBaseSlug(trimmed);
  if (base) {
    // "<slug>:<id>" conocido → label del slug sin exponer el id; desconocido → genérico.
    return STOCK_MOVEMENT_REASONS[base] ?? FALLBACK_MOVEMENT_LABEL;
  }
  return STOCK_MOVEMENT_REASONS[trimmed] ?? FALLBACK_MOVEMENT_LABEL;
}

export type StockMovementReasonKey = keyof typeof STOCK_MOVEMENT_REASONS;

/**
 * Reasons que permiten dejar stock negativo al ajustar.
 * Razones operacionales donde el negativo es válido (ej. conteo físico, merma, transferencia).
 * Compartido entre adjustStockInConnection (SQLite) y adjustStock (ramOnly) para evitar drift.
 */
export const REASONS_ALLOWING_NEGATIVE_STOCK: ReadonlySet<string> = new Set([
  'physical_count',
  'shrinkage',
  'breakage',
  'return',
  'purchase_return',
  'transfer_out',
  'transfer_void_out',
  'purchase_reception_void',
  'variant_deactivated',
]);

/** Devuelve true si el reason permite stock negativo. */
export function reasonAllowsNegativeStock(reason: string): boolean {
  return REASONS_ALLOWING_NEGATIVE_STOCK.has(reason);
}

/** True si el movimiento corresponde a una venta (sale:<id>, sale_void:<id> o sale legado). */
export function isSaleStockMovementReason(reason: string | null | undefined): boolean {
  const r = (reason ?? '').trim();
  return r === 'sale' || r.startsWith('sale:') || r.startsWith('sale_void:');
}

/**
 * Extrae saleId de un movimiento de stock.
 * Solo cuando el movimiento ES una venta: `reference_id` de un `work_order`
 * u otro motivo apunta a otra entidad — no puede producir un link "Ver venta".
 */
export function extractSaleIdFromStockMovement(params: {
  reason?: string | null;
  referenceId?: string | null;
}): string | null {
  const reason = (params.reason ?? '').trim();
  if (!isSaleStockMovementReason(reason)) return null;
  const ref = params.referenceId?.trim();
  if (ref) return ref;
  if (reason.startsWith('sale:')) return reason.slice('sale:'.length);
  if (reason.startsWith('sale_void:')) return reason.slice('sale_void:'.length);
  return null;
}

/**
 * Extrae workOrderId de un movimiento `work_order` (deducción de insumos de una OT).
 * `reference_id` guarda el id de la orden de trabajo.
 */
export function extractWorkOrderIdFromStockMovement(params: {
  reason?: string | null;
  referenceId?: string | null;
}): string | null {
  if ((params.reason ?? '').trim() !== 'work_order') return null;
  const ref = params.referenceId?.trim();
  return ref ? ref : null;
}
